import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { getAdminDashboard } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Users,
  UserCheck,
  UserPlus,
  UserCog,
  Shield,
  UserPlus2,
  MessageSquare,
  Megaphone,
  ArrowRight,
} from "lucide-react";

const StatCard = ({ title, value, icon: Icon, href, color = "slate" }) => {
  const colorMap = {
    slate: "bg-slate-100 text-slate-600",
    sky: "bg-sky-100 text-sky-600",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
    violet: "bg-violet-100 text-violet-600",
    rose: "bg-rose-100 text-rose-600",
  };
  const cls = colorMap[color] || colorMap.slate;

  return (
    <Card className="border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
          </div>
          <div className={`h-12 w-12 rounded-full flex items-center justify-center ${cls}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        {href && (
          <Link to={href} className="mt-4 block">
            <Button variant="ghost" size="sm" className="text-slate-600 -ml-2">
              Ver
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminDashboard();
      const d = res?.data;
      setStats(d && typeof d === "object" ? d : null);
    } catch (err) {
      setStats(null);
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Panel de Administración</h1>
        <p className="text-slate-500 mt-1">Resumen de usuarios, contactos y feedback</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total usuarios"
          value={stats?.total_registered_users ?? 0}
          icon={Users}
          href="/admin/users"
          color="slate"
        />
        <StatCard
          title="Usuarios activos"
          value={stats?.active_users ?? 0}
          icon={UserCheck}
          href="/admin/users"
          color="emerald"
        />
        <StatCard
          title="Usuarios en prueba"
          value={stats?.trial_users ?? 0}
          icon={UserPlus}
          href="/admin/users"
          color="amber"
        />
        <StatCard
          title="Usuarios gratuitos"
          value={stats?.free_users ?? 0}
          icon={UserCog}
          href="/admin/users"
          color="violet"
        />
        <StatCard
          title="Administradores"
          value={stats?.admin_users ?? 0}
          icon={Shield}
          color="slate"
        />
        <StatCard
          title="Contactos nuevos"
          value={stats?.new_contacts ?? 0}
          icon={UserPlus2}
          href="/admin/contacts"
          color="sky"
        />
        <StatCard
          title="Feedback pendiente"
          value={stats?.feedback_pending ?? 0}
          icon={MessageSquare}
          href="/admin/feedback"
          color="rose"
        />
        <StatCard
          title="Campañas activas"
          value={stats?.active_advertising_campaigns ?? 0}
          icon={Megaphone}
          href="/admin/advertising"
          color="emerald"
        />
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Accesos rápidos</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Link to="/admin/users">
            <Button variant="outline" className="border-slate-200">
              Gestionar usuarios
            </Button>
          </Link>
          <Link to="/admin/billing">
            <Button variant="outline" className="border-slate-200">
              Facturación
            </Button>
          </Link>
          <Link to="/admin/contacts">
            <Button variant="outline" className="border-slate-200">
              Contactos
            </Button>
          </Link>
          <Link to="/admin/feedback">
            <Button variant="outline" className="border-slate-200">
              Feedback
            </Button>
          </Link>
          <Link to="/admin/advertising">
            <Button variant="outline" className="border-slate-200">
              Publicidad
            </Button>
          </Link>
          <Link to="/admin/analytics">
            <Button variant="outline" className="border-slate-200">
              Analytics
            </Button>
          </Link>
          <Link to="/admin/settings">
            <Button variant="outline" className="border-slate-200">
              Configuración
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
